import React, { useState } from 'react';
import styled from 'styled-components';
import CheckCircleFilled from '../../assets/icons/checkCircleFilled.svg?react';
import { Typography } from '../Typography/Typography';
import Button from '../Button/Button';
import { popoverBoxshadow } from '../../sharedStyles';

const StyledCornerNotificationCard = styled.div`
  display: flex;
  max-width: 326px;
  padding: 32px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  border-radius: var(--border-radius-medium);
  border: 1px solid var(--grayscale-100);
  background: var(--white);
  ${popoverBoxshadow}
  box-sizing: border-box;
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  align-self: stretch;
`;

const IconContainer = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export type CornerNotificationCardProps = {
  /**
   * Class name for additional styling
   */
  className?: string;
  /**
   * Variant type for the notification (only 'success' supported currently)
   */
  variant: 'success';
  /**
   * Title text displayed next to the icon
   */
  title: string;
  /**
   * Content node displayed below the title
   */
  content: React.ReactNode;
  /**
   * Text for the dismiss button at the bottom
   */
  dismissButtonText?: string;
  /**
   * Click handler for the dismiss button
   */
  onDismiss: () => void;
};

/**
 * Displays a notification card in the bottom-right corner of the screen
 * with an icon, title, content, and dismiss button.
 */
export const CornerNotificationCard = ({
  className,
  variant,
  title,
  content,
  dismissButtonText = 'Dismiss message',
  onDismiss,
}: CornerNotificationCardProps) => {
  const [visible, setVisible] = useState(true);

  if (!visible) {
    return null;
  }

  // Dismiss immediately and call the onDismiss callback
  const handleDismiss = () => {
    setVisible(false);
    onDismiss();
  };

  const renderIcon = () => {
    if (variant === 'success') {
      return <CheckCircleFilled title="success" color="var(--green-400)" width={24} height={24} />;
    }
    return null;
  };

  return (
    <StyledCornerNotificationCard className={className}>
      <HeaderSection>
        <TitleRow>
          <IconContainer>{renderIcon()}</IconContainer>
          <Typography styledAs="h6" renderedAs="h1" weight={500}>
            {title}
          </Typography>
        </TitleRow>
        {content}
      </HeaderSection>
      <Button variant="primary" size="xs" onClick={handleDismiss}>
        {dismissButtonText}
      </Button>
    </StyledCornerNotificationCard>
  );
};

export default CornerNotificationCard;
